import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ref, get, update } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { LuTrophy, LuClock } from "react-icons/lu";

const INPUT_CLASS =
  "w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-white text-black dark:text-black border border-gray-200 dark:border-0 text-sm";

const ManageChallenge = () => {
  const { monthKey } = useParams();
  const { currentUser } = useUser();
  const navigate = useMoveTo();

  const [form, setForm] = useState({
    monthKey: monthKey || "",
    title: "",
    description: "",
    prizeType: "cash",
    prizeValue: "",
    deadline: "",
    status: "active",
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!!monthKey);

  useEffect(() => {
    if (monthKey) {
      get(ref(database, `Challenges/${monthKey}`))
        .then((snap) => {
          if (snap.exists()) {
            const data = snap.val();
            setForm({
              monthKey: monthKey,
              title: data.title || "",
              description: data.description || "",
              prizeType: data.prize?.type || "cash",
              prizeValue: data.prize?.value || "",
              deadline: data.deadline ? new Date(data.deadline).toISOString().slice(0, 16) : "",
              status: data.status || "active",
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Load error:", err);
          setLoading(false);
        });
    }
  }, [monthKey]);

  const set_ = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isValid =
    form.monthKey.trim() &&
    form.title.trim() &&
    form.description.trim() &&
    form.prizeValue.trim() &&
    form.deadline;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      const challengeRef = ref(database, `Challenges/${form.monthKey}`);
      
      // If creating new, check if month exists
      if (!monthKey) {
          const snap = await get(challengeRef);
          if (snap.exists()) {
              window.showToast("Challenge already exists for this month!", "error");
              setSubmitting(false);
              return;
          }
      }

      const challengeData = {
        title: form.title.trim(),
        description: form.description.trim(),
        prize: {
          type: form.prizeType,
          value: form.prizeValue.trim(),
        },
        deadline: new Date(form.deadline).getTime(),
        status: form.status,
        monthKey: form.monthKey,
        updatedAt: Date.now(),
      };

      if (!monthKey) {
        challengeData.createdAt = Date.now();
        challengeData.createdBy = currentUser.uid;
      }

      await update(challengeRef, challengeData);
      window.showToast(monthKey ? "Challenge updated!" : "Challenge created!", "success");
      navigate(ROUTES.ADMIN_CHALLENGES);
    } catch (e) {
      console.error("ManageChallenge error:", e);
      window.showToast("Failed to save. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center dark:bg-[#1A1A1A] dark:text-white">Loading...</div>;

  return (
    <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
      <MobileHeader title={monthKey ? "Edit Challenge" : "Create Challenge"} isBackBtn />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 md:block hidden"
            >
              <img
                src={ICONS.arrowBack}
                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
              {monthKey ? "Edit Challenge" : "New Challenge"}
            </h2>
            <div className="w-5" />
          </div>

          <div className="max-w-lg mx-auto space-y-5">
            {/* Month and Status */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Month Key *</label>
                  <input
                    value={form.monthKey}
                    onChange={(e) => set_("monthKey", e.target.value)}
                    placeholder="YYYY-MM"
                    disabled={!!monthKey}
                    className={INPUT_CLASS + (monthKey ? " opacity-60 cursor-not-allowed" : "")}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => set_("status", e.target.value)}
                    className={INPUT_CLASS + " appearance-none"}
                  >
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
            </div>

            {/* Title */}
            <div>
              <label className="text-sm font-medium mb-2 block">Challenge Title *</label>
              <input
                value={form.title}
                onChange={(e) => set_("title", e.target.value)}
                placeholder="e.g. 30-Day VFX Mastery"
                className={INPUT_CLASS}
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <textarea
                value={form.description}
                onChange={(e) => set_("description", e.target.value)}
                rows={5}
                placeholder="Explain the challenge rules and goals..."
                className={INPUT_CLASS + " resize-none"}
              />
            </div>

            {/* Prize */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Prize Type</label>
                  <div className="flex bg-[#F5F9FE] dark:bg-white p-1 rounded-xl border border-gray-200 dark:border-0">
                    {["cash", "zylen"].map((t) => (
                      <button
                        key={t}
                        onClick={() => set_("prizeType", t)}
                        className={`flex-1 py-2 text-xs font-bold uppercase rounded-lg transition-all ${
                          form.prizeType === t
                            ? "bg-[#327691] text-white shadow-sm"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Prize Value *</label>
                  <input
                    value={form.prizeValue}
                    onChange={(e) => set_("prizeValue", e.target.value)}
                    placeholder="e.g. 500"
                    className={INPUT_CLASS}
                  />
                </div>
            </div>

            {/* Deadline */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center justify-between">
                <span>Submission Deadline *</span>
                <LuClock className="w-4 h-4 text-gray-400" />
              </label>
              <input
                type="datetime-local"
                value={form.deadline}
                onChange={(e) => set_("deadline", e.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              className="w-full py-4 rounded-xl bg-[#327691] text-white font-bold text-base shadow-lg shadow-[#327691]/20 disabled:opacity-40 transition-all mt-4"
            >
              {submitting ? "Saving..." : "Save Challenge Parameters"}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageChallenge;
